import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Badge,
  Button,
  Callout,
  Card,
  Flex,
  Heading,
  Text,
  TextField,
} from "@radix-ui/themes";
import {
  ArrowLeftIcon,
  CheckCircledIcon,
  InfoCircledIcon,
} from "@radix-ui/react-icons";
import { creditUser, getUser, getUserBilling } from "../api/admin";
import { usd, num, tokens, dateTime, date, dollarsToMcents } from "../lib/format";

export default function UserDetail() {
  const { id = "" } = useParams();
  const qc = useQueryClient();

  const user = useQuery({
    queryKey: ["user", id],
    queryFn: () => getUser(id),
    enabled: !!id,
  });
  const billing = useQuery({
    queryKey: ["billing", id],
    queryFn: () => getUserBilling(id),
    enabled: !!id,
  });

  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [flash, setFlash] = useState<string | null>(null);

  const credit = useMutation({
    mutationFn: () =>
      creditUser(id, dollarsToMcents(Number(amount)), reason || undefined),
    onSuccess: (res) => {
      setFlash(
        res.credited
          ? `Credited. New balance ${usd(res.balanceMcents)}.`
          : "No change (duplicate or zero).",
      );
      setAmount("");
      setReason("");
      qc.invalidateQueries({ queryKey: ["billing", id] });
    },
  });

  const amountNum = Number(amount);
  const amountValid = Number.isFinite(amountNum) && amountNum > 0;

  return (
    <Flex direction="column" gap="4">
      <Link to="/users" style={{ textDecoration: "none" }}>
        <Flex align="center" gap="1">
          <ArrowLeftIcon />
          <Text size="2" className="hq-muted">
            Back to users
          </Text>
        </Flex>
      </Link>

      {/* Account card */}
      <Card size="3">
        {user.isLoading ? (
          <Text className="hq-muted">Loading…</Text>
        ) : user.isError ? (
          <Text color="red">{String(user.error as Error)}</Text>
        ) : user.data ? (
          <Flex direction="column" gap="2">
            <Flex align="center" gap="2" wrap="wrap">
              <Heading size="5">{user.data.email}</Heading>
              {user.data.emailVerified ? (
                <Badge color="green">verified</Badge>
              ) : (
                <Badge color="amber">unverified</Badge>
              )}
            </Flex>
            <Flex gap="5" wrap="wrap">
              <Field label="Name" value={user.data.name ?? "—"} />
              <Field label="User ID" value={user.data.id} mono />
              <Field label="Joined" value={date(user.data.createdAt)} />
              <Field label="Updated" value={date(user.data.updatedAt)} />
            </Flex>
          </Flex>
        ) : null}
      </Card>

      {/* Wallet + credit */}
      <Card size="3">
        <Flex direction="column" gap="3">
          <Heading size="4">Wallet</Heading>
          {billing.isLoading ? (
            <Text className="hq-muted">Loading…</Text>
          ) : billing.isError ? (
            <Text color="red">{String(billing.error as Error)}</Text>
          ) : billing.data ? (
            <Flex gap="5" wrap="wrap">
              <Field
                label="Balance"
                value={usd(billing.data.wallet.balance_mcents)}
                big
              />
              <Field
                label="Period used"
                value={usd(billing.data.wallet.period_used_mcents)}
              />
              <Field
                label="Period cap"
                value={
                  billing.data.wallet.period_cap_mcents != null
                    ? usd(billing.data.wallet.period_cap_mcents)
                    : "none"
                }
              />
            </Flex>
          ) : null}

          {flash && (
            <Callout.Root color="green" size="1">
              <Callout.Icon>
                <CheckCircledIcon />
              </Callout.Icon>
              <Callout.Text>{flash}</Callout.Text>
            </Callout.Root>
          )}

          <Flex gap="2" align="end" wrap="wrap">
            <label>
              <Text size="1" color="gray" mb="1" as="div">
                Credit amount (USD)
              </Text>
              <TextField.Root
                type="number"
                step="0.01"
                min="0"
                placeholder="5.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                style={{ width: "8rem" }}
              />
            </label>
            <label style={{ flex: "1 1 12rem" }}>
              <Text size="1" color="gray" mb="1" as="div">
                Reason (optional)
              </Text>
              <TextField.Root
                placeholder="Support comp"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            <Button
              onClick={() => credit.mutate()}
              disabled={!amountValid || credit.isPending}
              loading={credit.isPending}
            >
              Credit wallet
            </Button>
          </Flex>
          {credit.isError && (
            <Text size="2" color="red">
              {String(credit.error as Error)}
            </Text>
          )}
        </Flex>
      </Card>

      {/* Ledger */}
      <Card size="3">
        <Flex direction="column" gap="2">
          <Heading size="4">Ledger</Heading>
          {billing.data && billing.data.ledger.length === 0 && (
            <Callout.Root size="1" color="gray">
              <Callout.Icon>
                <InfoCircledIcon />
              </Callout.Icon>
              <Callout.Text>No ledger entries yet.</Callout.Text>
            </Callout.Root>
          )}
          {billing.data && billing.data.ledger.length > 0 && (
            <table className="hq-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Kind</th>
                  <th>Model</th>
                  <th className="num">Tokens</th>
                  <th className="num">Amount</th>
                  <th className="num">Balance</th>
                </tr>
              </thead>
              <tbody>
                {billing.data.ledger.map((e) => (
                  <tr key={e.id}>
                    <td>{dateTime(e.created_at)}</td>
                    <td>
                      <Badge
                        color={e.amount_mcents >= 0 ? "green" : "gray"}
                        variant="soft"
                      >
                        {e.kind}
                      </Badge>
                    </td>
                    <td>{e.model ?? <span className="hq-muted">—</span>}</td>
                    <td className="num">
                      {e.input_tokens != null || e.output_tokens != null ? (
                        <>
                          {tokens(e.input_tokens)}↑ {tokens(e.output_tokens)}↓
                          {e.cached_input_tokens
                            ? ` (${num(e.cached_input_tokens)} cached)`
                            : ""}
                        </>
                      ) : (
                        <span className="hq-muted">—</span>
                      )}
                    </td>
                    <td
                      className="num"
                      style={{
                        color:
                          e.amount_mcents >= 0
                            ? "var(--green-11)"
                            : "var(--gray-12)",
                      }}
                    >
                      {usd(e.amount_mcents)}
                    </td>
                    <td className="num">{usd(e.balance_after_mcents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Flex>
      </Card>
    </Flex>
  );
}

function Field({
  label,
  value,
  mono,
  big,
}: {
  label: string;
  value: string;
  mono?: boolean;
  big?: boolean;
}) {
  return (
    <Flex direction="column" gap="1">
      <Text size="1" className="hq-muted">
        {label}
      </Text>
      <Text
        size={big ? "6" : "2"}
        weight={big ? "bold" : "regular"}
        style={mono ? { fontFamily: "var(--code-font-family, monospace)" } : undefined}
      >
        {value}
      </Text>
    </Flex>
  );
}
